'use client';

import { cn } from '@sharpr/ui';

interface ParkFactorBarsProps {
  factors: {
    lhb: { runs: number; so: number; bb: number; h: number; hr: number };
    rhb: { runs: number; so: number; bb: number; h: number; hr: number };
  };
  compact?: boolean;
}

const STATS = [
  { key: 'hr' as const, label: 'HR' },
  { key: 'h' as const, label: 'H' },
  { key: 'runs' as const, label: 'R' },
  { key: 'so' as const, label: 'K', invert: true },
  { key: 'bb' as const, label: 'BB' },
];

function factorValueColor(absDev: number, isSignificant: boolean, isHitterFriendly: boolean): string {
  if (absDev <= 2) return 'text-gray-400';
  if (!isSignificant) return 'text-gray-400';
  return isHitterFriendly ? 'text-red-400' : 'text-emerald-400';
}

function FactorBar({ value, label, invert }: { value: number; label: string; invert?: boolean }) {
  const deviation = value - 100;
  const absDev = Math.abs(deviation);
  const barPct = Math.min(absDev * 2.5, 50);
  const isHitterFriendly = invert ? deviation < 0 : deviation > 0;
  const isSignificant = absDev >= 1;

  return (
    <div className="flex items-center gap-1">
      <span className="text-[9px] text-gray-400 w-5 text-right font-semibold">{label}</span>
      <div className="flex-1 h-2.5 bg-white/[0.06] rounded relative overflow-hidden">
        {/* Center marker */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/15 z-10" />
        {/* Bar from center */}
        {isSignificant && (
          <div
            className={cn(
              'absolute top-0 h-full',
              isHitterFriendly ? 'bg-red-500/50' : 'bg-emerald-500/50'
            )}
            style={{
              width: `${barPct}%`,
              ...(deviation > 0 ? { left: '50%' } : { right: '50%' }),
            }}
          />
        )}
      </div>
      <span className={cn(
        'text-[9px] font-mono w-7 text-right font-semibold',
        factorValueColor(absDev, isSignificant, isHitterFriendly)
      )}>
        {deviation === 0 ? '0' : (deviation > 0 ? '+' : '') + deviation}
      </span>
    </div>
  );
}

export function ParkFactorBars({ factors, compact }: ParkFactorBarsProps) {
  const avg = STATS.map(s => ({
    ...s,
    value: Math.round((factors.lhb[s.key] + factors.rhb[s.key]) / 2),
  }));

  const display = compact ? avg.filter(s => ['hr', 'h', 'runs'].includes(s.key)) : avg;

  return (
    <div className="space-y-0.5">
      {display.map((s) => (
        <FactorBar key={s.key} value={s.value} label={s.label} invert={s.invert} />
      ))}
    </div>
  );
}
